import React, { FC, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import useDebounce from '../../../hooks/useDebounce';
import refProps from '../../../interfaces/refProps';
import styles from './RegisterPassword.module.scss';
import validate from '../../../utils/validate';
import checkMark from '../../../styles/images/check-mark.svg';
import eyeHide from '../../../styles/images/eye-hide.svg';
import eyeShow from '../../../styles/images/eye-show.svg';
import bullet from '../../../styles/images/filled-circle.svg';
import { useHistory, useLocation } from 'react-router-dom';
import registerProps from '../../../interfaces/registerProps';
import dumbleDataProps from '../../../interfaces/dumbleDataProps';
import useDumbleData from '../../../hooks/useDumbleData';
import useRegisterApi from '../../../hooks/useRegisterApi';
import buildRegisterBody from '../../../utils/register-body-builder';
import { ConfigContext } from '../../../providers/ConfigContext';
import useVerifyRecaptchaToken from '../../../hooks/useVerifyRecaptchaToken';


const RegisterPassword = (props: refProps) => {
  const { referrer, chat, reg } = props;
  
  let history = useHistory();
  const passwordDebouncer = useDebounce();
  const confirmedPasswordDebouncer = useDebounce();
  const [show, setShow] = useState(false);

  const ref = useRef(null);
  const location = useLocation<registerProps>();
  const [verifyRecaptchaToken] = useVerifyRecaptchaToken("");
  const [firstName, setFirstName] = useState(location.state?.firstName);
  const [lastName, setLastName] = useState(location.state?.lastName);
  const { reCaptchaSiteKey, reCaptchaEnabled } = useContext(ConfigContext);
  const [email, setEmail] = useState(location.state?.email);
  const [reCaptchaToken, setReCaptchaToken] = useState("");
  const [status, setStatus] = useState(0);
  const [showReCaptcha, setShowReCaptcha] = useState(reCaptchaEnabled);
  const [disabled, setDisabled] = useState(true); //button disabled
  const [isLoading, setIsLoading] = useState(false); // spinner
  const [showPassword, setShowPassword] = useState(false);
  const [tos, setTos] = useState(false);
  const [target, setTarget] = useState(null);
  const [registerUser] = useRegisterApi();
  const scholasticPrivacyLink: string = 'http://www.scholastic.com/privacy.htm';
  const scholasticTermsLink: string = 'http://www.scholastic.com/terms.htm';

  const [password, setPassword]= useState({
    value: '',
    error: 'This field is required',
    touched: false
  })

  const [confirmedPassword, setConfirmedPassword]= useState({
    value: '',
    error: '',
    touched: false
  })

  const [recaptchaError, setRecaptchaError] = useState('');

  const handleShowPassword = ()=>{
    setShowPassword(!showPassword);
  }

  const dumbleDataHook = useDumbleData;
  let dumbledata: dumbleDataProps;

  useEffect(()=> {
    if(referrer != "cptk"){
      let pageData = {
        name: 'MyScholastic:Registration:My Password',
        type: 'Registration',
        siteSectionLevel1: 'MyScholastic:Registration:My Password'
      };
      dumbledata = dumbleDataHook('page', pageData);
    } else {
      let pageData = {
        name: 'MyScholastic:Registration:Chairperson:My Password',
        type: 'Registration',
        siteSectionLevel1: 'MyScholastic:Registration:Chairperson:My Password'
      };
      dumbledata = dumbleDataHook('page', pageData);
    }
  }, []);

  // Handle state restoration when returning from MyRole
  useEffect(() => {
    if (location.state?.returningFromMyRole) {
      console.log("🔄 Restoring form state from MyRole navigation...");
      
      // Restore form fields if they exist in location state
      if (location.state.firstName && !firstName) {
        setFirstName(location.state.firstName);
      }
      if (location.state.lastName && !lastName) {
        setLastName(location.state.lastName);
      }
      if (location.state.email && !email) {
        setEmail(location.state.email);
      }
      if (location.state.password) {
        setPassword({
          value: location.state.password,
          error: '',
          touched: true
        });
      }
      
      console.log("✅ Form state restored");
    }
  }, [location.state]);

  useEffect(()=>{
    const timeout = passwordDebouncer(
      setPassword.bind(
        RegisterPassword,
        handleInput('error', (validate(password.value, ['password'])), password)
      ),
      300
    )
    return () => clearTimeout(timeout);
  },[password.value]);

  useEffect(()=>{
    const timeout = confirmedPasswordDebouncer(
      checkPassword,
      300
    )
    return () => clearTimeout(timeout);
  },[password.value, confirmedPassword.value]);

  function timeout(delay: number) {
    return new Promise( res => setTimeout(res, delay) );
  } 

  const checkPassword = (()=>{
    if ((password.value == confirmedPassword.value || !confirmedPassword.touched)){
      setConfirmedPassword(handleInput('error', "", confirmedPassword));
    } else if ((password.value !== confirmedPassword.value && confirmedPassword.touched)){
      setConfirmedPassword(handleInput('error', "Your password did not match. Try again.", confirmedPassword));
    }
  })

  useEffect(()=>{
    setDisabled(
      password.value !== confirmedPassword.value ||
      password.error !== "" ||
      confirmedPassword.error !== ""
    );
  },[password, confirmedPassword]);

  const handleInput = ((type: string, input: any, field: object) => {
    const newField: any = {...field};
    newField[type] = input;
    return newField;
  })

  const redirectMyRole = () => {
    if(referrer == "cptk" || referrer == "lp" || referrer == "ror"){
      register();
    } else {
      history.push({
        pathname: '/register/my-role.html',
        state: {
          password: password.value,
          firstName: firstName,
          lastName: lastName,
          email: email
        }
      });
    }
  }


  const register = async () => {
    const {data, error} = await registerUser(buildRegisterBody(firstName,lastName,password.value,email, "", "parent"), referrer);
    if(error != null){
      console.log(error);
      setStatus(error.status);
    } else {
      console.log(data);
      setStatus(200);
      history.push({
        pathname: '/register/welcome.html',
        state: {user: data, username: email, password: password}
      });
    }
  }
  
  // Extract callback setup into reusable function
  const setupRecaptchaCallback = useCallback(() => {
    (window as any).onRecaptchaSubmit = async (token: string) => {
      setRecaptchaError('');

      if (!showReCaptcha) {
        redirectMyRole();
        return;
      }

      if (token && typeof token === 'string') {
        setReCaptchaToken(token);
        const {data, error} = await verifyRecaptchaToken(token);
        if(data === true){
          redirectMyRole();
        } else {
          setRecaptchaError("reCAPTCHA verification failed. Please reload and try again.");
        }
      } else {
        setReCaptchaToken("");
        setRecaptchaError("reCAPTCHA verification failed. Please reload and try again.");
      }
    };
  }, [showReCaptcha, email, password, referrer, verifyRecaptchaToken, redirectMyRole]);

  // Consolidated reCAPTCHA initialization function
  const initializeRecaptcha = useCallback((context: string = "general") => {
    console.log(`🔄 Initializing reCAPTCHA (${context})...`);
    
    const button = document.getElementById('register-password-next');
    if (!button) return;

    // Update button styling and disabled state
    button.classList.add(styles.button, styles.purple);
    if (disabled) {
      button.classList.add(styles.disabled);
      button.setAttribute('disabled', 'true');
    } else {
      button.classList.remove(styles.disabled);
      button.removeAttribute('disabled');
    }
    
    // Setup callback if missing
    if (typeof (window as any).onRecaptchaSubmit !== 'function') {
      console.log("⚠️ Callback missing, setting up...");
      setupRecaptchaCallback();
    }

    // reCAPTCHA Enterprise initialization
    if ((window as any).grecaptcha && (window as any).grecaptcha.enterprise) {
      (window as any).grecaptcha.enterprise.ready(() => {
        try {
          console.log(`🔄 Google reCAPTCHA Enterprise is ready (${context})...`);
          
          // Check if the button already has a reCAPTCHA widget
          const hasWidget = button.querySelector('div[data-sitekey]') ||
                          button.querySelector('iframe[src*="recaptcha"]') ||
                          button.hasAttribute('data-widget-id');
          
          if (!hasWidget) {
            console.log(`🎯 Creating reCAPTCHA widget (${context})...`);
            
            // Ensure button has the correct attributes
            button.classList.add('g-recaptcha');
            button.setAttribute('data-sitekey', reCaptchaSiteKey);
            button.setAttribute('data-callback', 'onRecaptchaSubmit');
            button.setAttribute('data-action', 'signup');
            
            try {
              // Create the reCAPTCHA widget
              const widgetId = (window as any).grecaptcha.enterprise.render(button, {
                'sitekey': reCaptchaSiteKey,
                'callback': 'onRecaptchaSubmit',
                'action': 'signup',
                'size': 'invisible'
              });
              
              if (widgetId !== undefined) {
                button.setAttribute('data-widget-id', widgetId);
                console.log(`✅ reCAPTCHA Enterprise widget created (${context}) with ID:`, widgetId);
              }
            } catch (renderError) {
              console.log(`⚠️ reCAPTCHA Enterprise render failed (${context}):`, renderError);
              // Fallback - ensure button has attributes for auto-binding
              button.classList.add('g-recaptcha');
              button.setAttribute('data-sitekey', reCaptchaSiteKey);
              button.setAttribute('data-callback', 'onRecaptchaSubmit');
              button.setAttribute('data-action', 'signup');
            }
          } else {
            console.log(`ℹ️ reCAPTCHA widget already exists (${context})`);
            // Ensure proper configuration
            button.classList.add('g-recaptcha');
            button.setAttribute('data-sitekey', reCaptchaSiteKey);
            button.setAttribute('data-callback', 'onRecaptchaSubmit');
            button.setAttribute('data-action', 'signup');
          }
          
          // Maintain disabled state after widget operations
          if (disabled) {
            button.classList.add(styles.disabled);
            button.setAttribute('disabled', 'true');
          } else {
            button.classList.remove(styles.disabled);
            button.removeAttribute('disabled');
          }
          
        } catch (error) {
          console.log(`⚠️ reCAPTCHA Enterprise setup failed (${context}):`, error);
          // Fallback - ensure attributes are set
          button.classList.add('g-recaptcha');
          button.setAttribute('data-sitekey', reCaptchaSiteKey);
          button.setAttribute('data-callback', 'onRecaptchaSubmit');
          button.setAttribute('data-action', 'signup');
          
          if (disabled) {
            button.classList.add(styles.disabled);
            button.setAttribute('disabled', 'true');
          }
        }
      });
    } else {
      console.log(`⚠️ Google reCAPTCHA Enterprise not ready (${context}), setting up for auto-binding...`);
      // Fallback: ensure the button has the correct attributes for auto-binding
      button.classList.add('g-recaptcha');
      button.setAttribute('data-sitekey', reCaptchaSiteKey);
      button.setAttribute('data-callback', 'onRecaptchaSubmit');
      button.setAttribute('data-action', 'signup');
      
      // Ensure disabled state is maintained
      if (disabled) {
        button.classList.add(styles.disabled);
        button.setAttribute('disabled', 'true');
      }
    }
  }, [reCaptchaSiteKey, disabled, setupRecaptchaCallback]);
  
  // Consolidated reCAPTCHA initialization with cleanup
  useEffect(() => {
    if (!showReCaptcha) return;
    console.log("🔧 Setting up reCAPTCHA initialization...");
    
    // Initialize immediately
    initializeRecaptcha("initial");
    
    // Add retry attempts with increasing delays for reCAPTCHA script loading
    const timeoutId1 = setTimeout(() => initializeRecaptcha("retry-100ms"), 100);
    const timeoutId2 = setTimeout(() => initializeRecaptcha("retry-500ms"), 500);
    const timeoutId3 = setTimeout(() => initializeRecaptcha("retry-1s"), 1000);
    const timeoutId4 = setTimeout(() => initializeRecaptcha("retry-2s"), 2000);
    
    // Verify the callback is set
    setTimeout(() => {
      if (typeof (window as any).onRecaptchaSubmit === 'function') {
        console.log("✅ RecaptchaSubmit callback successfully registered");
      } else {
        console.log("❌ RecaptchaSubmit callback registration failed");
      }
    }, 100);

    return () => {
      console.log("🧹 Cleaning up reCAPTCHA initialization");
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      delete (window as any).onRecaptchaSubmit;
    };
  }, [initializeRecaptcha]);

  // Enhanced initialization for MyRole returns
  useEffect(() => {
    if (!showReCaptcha || !location.state?.returningFromMyRole) return;
    if (location.state?.returningFromMyRole) {
      console.log("🔄 Detected return from MyRole, ensuring reCAPTCHA widget exists...");
      
      // Initialize immediately with additional retries for MyRole returns
      initializeRecaptcha("myRole-return");
      
      const timeoutId1 = setTimeout(() => initializeRecaptcha("myRole-retry-100ms"), 100);
      const timeoutId2 = setTimeout(() => initializeRecaptcha("myRole-retry-500ms"), 500);
      const timeoutId3 = setTimeout(() => initializeRecaptcha("myRole-retry-1s"), 1000);
      const timeoutId4 = setTimeout(() => initializeRecaptcha("myRole-retry-2s"), 2000);
      
      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        clearTimeout(timeoutId4);
      };
    }
  }, [location.state?.returningFromMyRole, initializeRecaptcha]);
  
  const redirectMyInfo = () => {
    history.push({
      pathname: '/register/my-info.html',
      state: {email: {value: email, error: '', touched: true},
      firstName: {value: firstName, error: '', touched: true},
      lastName: {value: lastName, error: '', touched: true}}
    });
  }

  const openToggle = async (event: any) =>{
    event.persist();
    await timeout(300);
    setShow(true);
    setTarget(event.target);
  }

  const closeToggle = () => {
    setShow(false);
  }

  return (
  <div className={styles.RegisterPassword} data-testid="RegisterPassword">
    <div className={styles.headerText}>My Password</div>
    <div className={styles.inputContainer}>
      <div className={styles.passwordContainer}>
        <i className={styles.eye}>
          <img 
            tabIndex={0}
            alt="show or hide password"
            src={showPassword ? eyeHide : eyeShow} 
            onClick={handleShowPassword}
            onKeyPress={(e) => {if(e.key === 'Enter' || e.key === ' '){handleShowPassword()}}}
          />
        </i>
        <input 
          autoFocus
          id="register-new-password"
          aria-label="password"
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          className={`${styles.inputGrey} ${password.error === '' || !password.touched ? '' : styles.errorField}`}
          onSelect={()=>setPassword(handleInput('touched', true, password))}
          onChange={(e) => setPassword(handleInput('value', e.target.value, password))}
          onFocus={(e)=>openToggle(e)}
          onBlur={()=>closeToggle()}
        />
      </div>
        <input 
          id="register-confirm-password"
          aria-label="confirm password"
          type="password"
          placeholder="Confirm Password"
          className={`${styles.inputGrey} ${confirmedPassword.error === '' || !confirmedPassword.touched ? '' : styles.errorField}`}
          onSelect={()=>setConfirmedPassword(handleInput('touched', true, confirmedPassword))}
          onChange={(e) => setConfirmedPassword(handleInput('value', e.target.value, confirmedPassword))}
        />
        <div data-testid="doesNotMatchErr" className={`${confirmedPassword.error !== '' ? styles.errorMessage : styles.disable}`}>{confirmedPassword.error}</div> {/* show this error if they're not the same passwords */}
        <div data-testid="recaptchaErr" className={`${recaptchaError !== '' ? styles.errorMessage : styles.disable}`}>{recaptchaError}</div> {/* show this error if reCAPTCHA verification fails */}
    </div>
    <div className={styles.passReq}>
      Your password must be at least seven characters long and include at least one letter and one number. It is case sensitive and special characters are allowed.
    </div>
    <div className={styles.tosContainer}>
      <div className={styles.tosText}>
        By clicking <span className={styles.tosNext}>NEXT</span>, I agree to Scholastic's <a href={scholasticTermsLink} target="_blank" rel="noopener noreferrer">TERMS OF USE</a> and <a href={scholasticPrivacyLink} target="_blank" rel="noopener noreferrer">PRIVACY POLICY</a>, and to receive emails and other communications from Scholastic.
      </div>
    </div>
    <div className={styles.buttonContainer}>
      <button
        id="register-password-back"
        aria-label="back password"
        className = {`${styles.button} ${styles.purple} ${disabled ? styles.disabled : ''}`}
        onClick={redirectMyInfo}
        >
          PREVIOUS
      </button>
      <button aria-label="next button"
        id="register-password-next"
        disabled={disabled}
        className = {`${showReCaptcha ? 'g-recaptcha' : ''} ${styles.button} ${styles.purple} ${disabled ? styles.disabled : ''}`}
        data-sitekey={showReCaptcha ? reCaptchaSiteKey : undefined}
        data-callback={showReCaptcha ? "onRecaptchaSubmit" : undefined}
        data-action={showReCaptcha ? "signup" : undefined}
        onClick={showReCaptcha ? undefined : redirectMyRole}
        >
          next
      </button>
    </div>

    <Overlay
        show={show}
        target={target}
        placement="bottom"
        container={ref.current}
        containerPadding={20}>
        <Popover id="popover-contained" className={styles.popup}>
          <Popover.Content>
            <div className={styles.pleaseText}>Please enter a password that meets all criteria</div>
            <div className={styles.popOverText}><div className={styles.createText}>To create a strong password use</div>
              <div className={styles.listItem}>
                <i className={`${validate(password.value, ['passwordMin']) === '' ? styles.checkmark : styles.bullet}`}><img 
                src={validate(password.value, ['passwordMin']) === '' ? checkMark : bullet} alt="password minimum length requirement checkmark"
                /></i>
                <div className={`${validate(password.value, ['passwordMin']) === '' ? styles.success : ''}`}>7 or more characters</div>
              </div>
              <div className={styles.listItem}>
                <i className={`${validate(password.value, ['passwordNumLetter']) === '' ? styles.checkmark : styles.bullet}`}><img 
                src={validate(password.value, ['passwordNumLetter']) === '' ? checkMark : bullet} alt="password number and letter requirement checkmark"
                /></i>
                <div className={`${validate(password.value, ['passwordNumLetter']) === '' ? styles.success : ''}`}>At least 1 letter and 1 number</div>
              </div>
              {/* // Suppressed until we communicate with other teams
              <div className={styles.listItem}>
                <i className={`${validate(password.password, ['oneUpperOneLower']) == '' ? styles.checkmark : styles.bullet}`}><img 
                src={validate(password.password, ['oneUpperOneLower']) == '' ? checkMark : bullet}
                /></i>
                <div className={`${validate(password.password, ['oneUpperOneLower']) == '' ? styles.success : ''}`}>Upper and lowercase letters</div>
              </div>
              <div className={styles.listItem}>
                <i className={`${validate(password.password, ['passwordSpecial']) == '' ? styles.checkmark : styles.bullet}`}><img 
                src={validate(password.password, ['passwordSpecial']) == '' ? checkMark : bullet}
                /></i>
                <div className={`${validate(password.password, ['passwordSpecial']) == '' ? styles.success : ''}`}>Special characters</div>
              </div>*/}
            </div>
          </Popover.Content>
        </Popover>
      </Overlay>
  </div>
);}

export default RegisterPassword;
